import { Mark, markPasteRule, mergeAttributes } from "@tiptap/core";
import Bold from "@tiptap/extension-bold";
import Document from "@tiptap/extension-document";
import Italic from "@tiptap/extension-italic";
import Paragraph from "@tiptap/extension-paragraph";
import Text from "@tiptap/extension-text";
import {
  BubbleMenu,
  Content,
  Editor,
  EditorContent,
  EditorEvents,
  JSONContent,
  useEditor,
} from "@tiptap/react";
import { useAppDispatch, useAppSelector } from "Redux/hooks";
import { resetPopUpType, setClosePopUp, setRichTextFocus, setShowPopUp } from "feature/producer/actions";
import debounce from "lodash.debounce";
import { useEffect, useState } from "react";
import { TextElement } from "types/types";
import { selectShowPopUp, setLinkToAdd, setTermToAdd } from "../../Redux/ProducerSlice";
import Button from "../Atoms/Button";
import { searchElementModifyText } from "./ElementSelector";
// @ts-ignore
import boldSvg from "../../images/bold-svg.svg";
// @ts-ignore
import italicSvg from "../../images/italic-svg.svg";
import {
  selectUserLinks,
  selectUserRefs,
  selectUserTerms,
} from "Redux/UserSlice";
import { selectStyle } from "Redux/appSlice";

function MenuBar({
  editor,
  element,
  children,
}: {
  editor: Editor | null;
  element: any;
  children: any;
}) {
  const dispatch = useAppDispatch();
  if (!editor) {
    return null;
  }

  const isSelected =
    editor.view.state.selection.ranges[0].$from.pos !==
    editor.view.state.selection.ranges[0].$to.pos;

  return (
    <div className="flex flex-start">
      <button
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={`border-2 w-8 p-2 ${
          editor.isActive("bold") ? "bg-gray-300 is-active" : ""
        }`}
      >
        <img src={boldSvg}></img>
      </button>
      <button
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={`border-2 p-2 w-8 ${
          editor.isActive("italic") ? "bg-gray-300 is-active" : ""
        }`}
      >
        <img src={italicSvg}></img>
      </button>
      <button
        onClick={() => {
          editor
            .chain()
            .focus()
            // @ts-ignore
            .toggleRef({
              "data-ref-id": "",
            })
            .run();

          const transaction = editor.state.tr.insertText(" ");
          editor.view.dispatch(transaction);
        }}
        className={`border-2 p-2 ${
          editor.isActive("ref") ? "bg-gray-300 is-active" : ""
        }`}
      >
        Ref
      </button>
      {isSelected && (
        <>
          <Button
            onClick={() => {
              dispatch(
                setTermToAdd({
                  callback: (termId) => {
                    dispatch(setClosePopUp());
                    dispatch(resetPopUpType());
                    editor
                      .chain()
                      .focus()
                      // @ts-ignore
                      .toggleTerm({ "data-term-id": termId })
                      .run();
                  },
                })
              );
            }}
            className={`text-sm !p-2 m-1  ${
              editor.isActive("term") ? "is-active" : ""
            }`}
            label="Term"
          />
          <Button
            onClick={() => {
              dispatch(
                setLinkToAdd({
                  callback: (linkId, linkData) => {
                    // use this only for closing, opening is handled elsewhere
                    dispatch(setClosePopUp());
                    dispatch(resetPopUpType());
                    editor
                      .chain()
                      .focus()
                      // @ts-ignore
                      .toggleLink({
                        "data-web-link-id": linkId,
                        link_data: linkData,
                      })
                      .run();
                  },
                })
              );
            }}
            className={`text-sm !p-2 m-1 ${
              editor.isActive("web-link") ? "is-active" : ""
            }`}
            label="Link"
          />
        </>
      )}
      {children}
    </div>
  );
}

function ElementsMenu({ editor }: { editor: Editor | null }) {
  const dispatch = useAppDispatch();

  if (!editor) {
    return null;
  }

  return (
    <BubbleMenu editor={editor} tippyOptions={{ duration: 100 }} className="">
      <Button
        onClick={() => {
          dispatch(
            setTermToAdd({
              callback: (termId) => {
                dispatch(setShowPopUp());
                editor
                  .chain()
                  .focus()
                  // @ts-ignore
                  .toggleTerm({ "data-term-id": termId })
                  .run();
              },
            })
          );
        }}
        className={`text-sm !p-2 m-1  ${
          editor.isActive("term") ? "is-active" : ""
        }`}
        label="Term"
      />
      <Button
        onClick={() => {
          dispatch(
            setLinkToAdd({
              callback: (linkId, linkData) => {
                dispatch(setShowPopUp());
                editor
                  .chain()
                  .focus()
                  // @ts-ignore
                  .toggleLink({
                    "data-web-link-id": linkId,
                    link_data: linkData,
                  })
                  .run();
              },
            })
          );
        }}
        className={`text-sm !p-2 m-1 ${
          editor.isActive("web-link") ? "is-active" : ""
        }`}
        label="Link"
      />
    </BubbleMenu>
  );
}

export function CompleteEditor({
  text,
  className,
  style,
  onUpdate,
  elementAskingRerender,
  element,
  menuBarSiblings,
}: {
  text: any;
  className?: string;
  style?: Record<string, any>;
  onUpdate: (jsonContent: JSONContent) => void;
  elementAskingRerender?: any[];
  element: Record<string, any>;
  menuBarSiblings?: any;
}) {
  const randomId = (Math.random() + 1).toString(36).substring(7);
  const dispatch = useAppDispatch();
  const userTerms = useAppSelector(selectUserTerms);
  const userRefs = useAppSelector(selectUserRefs);
  const userLinks = useAppSelector(selectUserLinks);
  const [insideText, setText] = useState(text);
  const [references, setReferences] = useState<
    Array<{ id: string; callback: (arg: any) => TextElement }>
  >([]);

  useEffect(() => {
    references.forEach((r) => {
      if (userRefs !== undefined) {
        if (userRefs[r.id] !== undefined) {
          setText(r.callback(userRefs[r.id].code));
        }
      }
    });
  }, [references]);

  useEffect(() => {
    const refs: Array<any> = [];
    const newText = JSON.parse(JSON.stringify(text));
    searchElementModifyText(
      newText,
      { element: "ref", data: "data-ref-id" },
      (r) => refs.push(r),
      newText
    );
    const toCheckLocalRefs = refs.map((r) => r.id);
    const toCheckGlobalRefs = references.map((r) => r.id);

    if (
      JSON.stringify(toCheckLocalRefs) !== JSON.stringify(toCheckGlobalRefs)
    ) {
      setReferences(refs);
    }
  }, [text]);

  useEffect(() => {
    setText(text);
  }, [...(elementAskingRerender || [])]);

  useEffect(() => {
    window.refs = userRefs;
    window.links = userLinks;
    window.terms = userTerms;
  }, [userTerms, userLinks, userRefs]);

  const editor = useMinimalEditor(
    insideText,
    (val) => onUpdate(val.editor.getJSON()),
    [...(elementAskingRerender || []), insideText]
  );

  return (
    <div className="w-full" id={randomId}>
      <MenuBar editor={editor} element={element}>
        {menuBarSiblings}
      </MenuBar>
      <EditorContent
        editor={editor}
        className={className}
        style={style}
        onFocus={() => {
          dispatch(setRichTextFocus(element));
        }}
      />
    </div>
  );
}

export function CoreEditor({
  text = "",
  onUpdate,
  elementAskingRerender = [],
  contentEditable = true,
  element,
}: {
  text: string;
  onUpdate?: (arg: any) => void;
  elementAskingRerender?: [];
  contentEditable: boolean;
  element?: any;
}) {
  const userTerms = useAppSelector(selectUserTerms);
  const userRefs = useAppSelector(selectUserRefs);
  const userLinks = useAppSelector(selectUserLinks);
  const dispatch = useAppDispatch();

  useEffect(() => {
    window.refs = userRefs;
    window.links = userLinks;
    window.terms = userTerms;
  }, [userTerms, userLinks, userRefs]);

  const editor = useMinimalEditor(
    text,
    (val) => {
      if (onUpdate) onUpdate(val.editor.getJSON());
    },
    elementAskingRerender
  );

  return (
    <div>
      {editor && <ElementsMenu editor={editor} />}
      <EditorContent
        editor={editor}
        contentEditable={contentEditable}
        onFocus={() => dispatch(setRichTextFocus(element))}
      />
    </div>
  );
}

function useMinimalEditor(
  content: Content,
  onUpdate: (props: EditorEvents["update"]) => void,
  elementAskingRerender: any[]
) {
  const { color2, color4 } = useAppSelector(selectStyle);

  return useEditor(
    {
      extensions: [
        Document,
        Text,
        Bold,
        Italic,
        Paragraph.configure({
          HTMLAttributes: {
            style: `color: ${color4 ? color4 : ""}`,
          },
        }),
        Term.configure({
          HTMLAttributes: {
            style: `color: ${
              color2 ? color2 : ""
            }; text-decoration: underline; cursor: pointer;`,
          },
        }),
        WebLink.configure({
          HTMLAttributes: {
            style: `color: black; text-decoration: underline; text-decoration-style: dotted; cursor: pointer;
text-underline-offset: 2px;
`,
          },
        }),
        Reference.configure({
          HTMLAttributes: {
            style: `
             background-color: #00FFFF;
             border-radius: 99px;
            `,
          },
        }),
      ],
      content: content,
      onUpdate: debounce((val: EditorEvents["update"]) => onUpdate(val), 200),
    },
    [...elementAskingRerender]
  );
}

export const Term = Mark.create({
  name: "term",

  priority: 1000,

  keepOnSplit: false,

  inclusive() {
    return this.options.autolink;
  },

  addOptions() {
    return {
      openOnClick: true,
      linkOnPaste: true,
      autolink: true,
      protocols: [],
      HTMLAttributes: {
        target: "_blank",
        rel: "noopener noreferrer nofollow",
      },
    };
  },

  addAttributes() {
    return {
      "data-term-id": {
        default: null,
      },
    };
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "a",
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
      0,
    ];
  },

  // @ts-ignore
  addCommands() {
    return {
      setTerm:
        (attributes: any) =>
        ({ chain }: any) => {
          return chain()
            .setMark(this.name, attributes)
            .setMeta("preventAutolink", true)
            .run();
        },

      // @ts-ignore
      toggleTerm:
        // @ts-ignore


          (attributes) =>
          // @ts-ignore
          ({ chain }) => {
            return chain()
              .toggleMark(this.name, attributes, { extendEmptyMarkRange: true })
              .setMeta("preventAutolink", true)
              .run();
          },

      unsetTerm:
        () =>
        ({ chain }: any) => {
          return chain()
            .unsetMark(this.name, { extendEmptyMarkRange: true })
            .setMeta("preventAutolink", true)
            .run();
        },
    };
  },

  addPasteRules() {
    return [
      markPasteRule({
        find: (text) =>
          // @ts-ignore
          find(text).map((term) => ({
            text: term.value,
            index: term.start,
            data: term,
          })),
        type: this.type,
        getAttributes: (match) => ({
          href: match.data?.href,
        }),
      }),
    ];
  },
});

export const WebLink = Mark.create({
  name: "web-link",

  priority: 1000,

  keepOnSplit: false,

  inclusive() {
    return this.options.autolink;
  },

  addOptions() {
    return {
      openOnClick: true,
      linkOnPaste: true,
      autolink: true,
      protocols: [],
      HTMLAttributes: {
        target: "_blank",
        rel: "noopener noreferrer nofollow",
      },
    };
  },

  addAttributes() {
    return {
      "data-web-link-id": {
        default: null,
      },
    };
  },

  renderHTML({ HTMLAttributes }) {
    const attributes = mergeAttributes(
      this.options.HTMLAttributes,
      HTMLAttributes
    );

    if (attributes.link_data) {
      attributes.href = attributes.link_data.address;
    } else if (attributes.links_id && window.links) {
      attributes.href = window.links[attributes.link_id].address;
    } else {
      attributes.href = "";
    }

    return ["a", attributes, 0];
  },

  // @ts-ignore
  addCommands() {
    return {
      setLink:
        (attributes: any) =>
        ({ chain }: any) => {
          return chain()
            .setMark(this.name, attributes)
            .setMeta("preventAutolink", true)
            .run();
        },

      // @ts-ignore
      toggleLink:
        // @ts-ignore


          (attributes) =>
          // @ts-ignore
          ({ chain }) => {
            return chain()
              .toggleMark(this.name, attributes, { extendEmptyMarkRange: true })
              .setMeta("preventAutolink", true)
              .run();
          },

      unsetLink:
        () =>
        ({ chain }: any) => {
          return chain()
            .unsetMark(this.name, { extendEmptyMarkRange: true })
            .setMeta("preventAutolink", true)
            .run();
        },
    };
  },

  addPasteRules() {
    return [
      markPasteRule({
        find: (text) =>
          // @ts-ignore
          find(text).map((link) => ({
            link: link.value,
            index: link.start,
            data: link,
          })),
        type: this.type,
        getAttributes: (match) => ({
          href: match.data?.href,
        }),
      }),
    ];
  },
});

export const Reference = Mark.create({
  name: "ref",

  priority: 1000,

  keepOnSplit: false,

  inclusive() {
    return this.options.autolink;
  },

  addOptions() {
    return {
      openOnClick: true,
      linkOnPaste: true,
      autolink: true,
      protocols: [],
      HTMLAttributes: {
        target: "_blank",
        rel: "noopener noreferrer nofollow",
      },
    };
  },

  addAttributes() {
    return {
      "data-ref-id": {
        default: null,
      },
    };
  },

  renderHTML({ HTMLAttributes }) {
    const attributes = {
      ...mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
      placeholder: "",
      readonly: true,
      autofocus: false,
      padding: "10px",
      id: "reference-span",
    } as const;

    if (
      window.refs !== undefined &&
      window.refs[HTMLAttributes["data-ref-id"]] !== undefined
    ) {
      // TODO: placeholder is read only
      // attributes.placeholder = window.refs[HTMLAttributes["data-ref-id"]].code;
    }

    return ["span", attributes];
  },

  // @ts-ignore
  addCommands() {
    return {
      setRef:
        (attributes: any) =>
        ({ chain }: any) => {
          return chain()
            .setMark(this.name, attributes)
            .setMeta("preventAutolink", true)
            .run();
        },

      toggleRef:
        // @ts-ignore


          (attributes) =>
          // @ts-ignore
          ({ chain }) => {
            return chain()
              .toggleMark(this.name, attributes, { extendEmptyMarkRange: true })
              .setMeta("preventAutolink", true)
              .run();
          },

      unsetRef:
        () =>
        ({ chain }: any) => {
          return chain()
            .unsetMark(this.name, { extendEmptyMarkRange: true })
            .setMeta("preventAutolink", true)
            .run();
        },
    };
  },

  addPasteRules() {
    return [
      markPasteRule({
        find: (text) =>
          // @ts-ignore
          find(text).map((ref: any) => ({
            text: ref.value,
            index: ref.start,
            data: ref,
          })),
        type: this.type,
        getAttributes: (match) => ({
          href: match.data?.href,
        }),
      }),
    ];
  },
});
